/**
 * Chart lifecycle logging, off unless asked for.
 *
 * A TradingView widget is expensive to build and its lifecycle is invisible:
 * when it rebuilds you see a flash and a reset viewport, with nothing in the
 * console saying why. A remount loop — a widget that tears down and rebuilds on
 * every render — looks from the outside like "the chart is slow", which is the
 * wrong thing to go and fix.
 *
 * So every mount, teardown, symbol change and resolution change is logged with
 * a per-instance id and a mount counter. Two mounts for one chart is a
 * rebuild; a counter climbing on its own is a loop, and the log line says which
 * dependency moved.
 *
 * Enable with `?chartdebug=1` on the URL, or `localStorage.owChartDebug = '1'`
 * to persist across navigations. Off, this costs one boolean read per call.
 */

let enabled: boolean | null = null

/** Whether chart debugging is on (`?chartdebug=1` or `localStorage.owChartDebug`). */
export function debugEnabled(): boolean {
	return isEnabled()
}

function isEnabled(): boolean {
	if (enabled !== null) {
		return enabled
	}
	if (typeof window === 'undefined') {
		return false
	}
	try {
		enabled =
			new URLSearchParams(window.location.search).get('chartdebug') === '1' ||
			window.localStorage.getItem('owChartDebug') === '1'
	} catch {
		// Private mode, blocked storage, or a malformed URL — not worth failing
		// a chart over.
		enabled = false
	}
	return enabled ?? false
}

/** Monotonic per-page, so two charts on one page are tellable apart. */
let nextId = 1
export function nextChartId(): string {
	return `chart#${nextId++}`
}

export function chartLog(id: string, event: string, detail?: Record<string, unknown>) {
	if (!isEnabled()) {
		return
	}
	// eslint-disable-next-line no-console
	console.log(
		`%c[tv-chart]%c ${id} ${event}`,
		'color:#9E77ED;font-weight:600',
		'color:inherit',
		detail ?? ''
	)
}

/**
 * Report which dependencies changed between renders.
 *
 * The point of the logger: when a widget rebuilds, this says what moved. A
 * dependency that is a fresh array or object literal every render shows up as
 * changed with visually identical values, which is the signature of the bug —
 * `denoms={['sats','usd']}` is a new array each time and will rebuild the
 * widget forever if it is a dependency.
 */
export function useDepChangeLog(
	id: string,
	label: string,
	deps: Record<string, unknown>
): void {
	if (!isEnabled() || typeof window === 'undefined') {
		return
	}
	const key = `__tvDeps_${id}_${label}`
	const store = window as unknown as Record<string, Record<string, unknown> | undefined>
	const prev = store[key]
	if (prev) {
		const changed = Object.keys(deps).filter((k) => !Object.is(prev[k], deps[k]))
		if (changed.length > 0) {
			chartLog(id, `${label} deps changed`, {
				changed,
				// Same-looking values with different identities are the tell.
				identityOnly: changed.filter(
					(k) => JSON.stringify(prev[k]) === JSON.stringify(deps[k])
				)
			})
		}
	}
	store[key] = { ...deps }
}
